import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Skeleton, Typography } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import RowMap from './rowMap'
import { doUpdateTenantMapping, fetchTenantMapping, fetchLinkedSource, fetchLinkedTarget, fetchLinkedConnector } from '../../redux/actions/network.actions'
import * as UIActions from '../../redux/actions/ui.actions';
import { Alert } from '../popupAlerts'
import { discoverLinkedTargetCatalog, discoverLinkedConnectorCatalog, discoverLinkedSourceCatalog, pollDiscoverLinkedConnector, pollDiscoverLinkedSource } from '../../api/client'
import { poll } from '@hotglue/common';
import { clearFieldMapSelection } from '../../redux/actions/ui.actions'
import { Add, Refresh, Warning } from '@mui/icons-material'
import moment from 'moment-mini'


const getAllProperties = (properties, arr, parentName = "") => {
  if (arr.length === 0)
    arr = arr.concat(properties && Object.keys(properties));

  let propsWithItems = []
  let propsWithProperties = []

  Object.keys(properties).forEach(prop => {
    if (!properties[prop]?.type) {
      return;
    }
    
    if (properties[prop].type?.includes('array') && !!properties[prop]?.items) {
      return propsWithItems.push({ ...properties[prop], name: prop })
    }

    if (properties[prop].type?.includes('object') && !!properties[prop]?.properties) {
      return propsWithProperties.push({ ...properties[prop], name: prop })
    }
  });

  propsWithItems?.forEach(prop => {
    if (prop.items.properties) {
      arr = getAllProperties(prop.items.properties, arr, parentName ? `${parentName}.${prop.name}` : prop.name)
      arr.push(...Object.keys(prop.items.properties).map(item => parentName ? `${parentName}.${prop.name}.${item}` : `${prop.name}.${item}`))
    }
  });

  propsWithProperties?.forEach(prop => {
    if (prop.properties) {
      arr = getAllProperties(prop.properties, arr, parentName ? `${parentName}.${prop.name}` : prop.name)
      arr.push(...Object.keys(prop.properties).map(item => parentName ? `${parentName}.${prop.name}.${item}` : `${prop.name}.${item}`))
    }
  });

  return arr;
}

const Mapping = (props) => {
  const tenantId = useSelector(state => state.configuration.tenant_id)
  const flowId = useSelector(state => state.flows.selectedFlow)
  const flowVersion = useSelector(state => state.flows.supportedFlows[flowId]?.version || 1)
  const listener = useSelector(state => state.configuration.listener)
  const schemas = useSelector(state => state.schemas[flowId])
  const localization = useSelector(state => state.configuration.localization)
  // const tap = useSelector(state => state.entities.selectedEntity.tap)
  const selectedEntity = useSelector(state => state.entities.selectedEntity)
  const multipleTables = useSelector(state => state.configuration.multipleTables)
  const currentEntity = useSelector(state => {
    if (selectedEntity?.entityType === 'target' && state?.targets?.linked)
      return state?.targets?.linked && state.targets?.linked[selectedEntity?.target]
    if (selectedEntity?.entityType === 'source')
      return state.sources?.linked && state?.sources?.linked[selectedEntity?.tap]

    return state.connectors?.linked && state?.connectors?.linked[selectedEntity?.id]
  })
  const updateFieldsOnMapping = useSelector(state => state.preferences.updateFieldsOnMapping)
  const entityId = useMemo(() => {
    if (selectedEntity?.entityType === 'target')
      return selectedEntity?.target
    if (selectedEntity?.entityType === 'source')
      return selectedEntity?.tap

    return selectedEntity?.id
  }, [selectedEntity])

  const entityType = useMemo(() => {
    if (flowVersion === 2)
      return 'connectors'
    if (selectedEntity?.entityType === 'target')
      return 'targets'
    if (selectedEntity?.entityType === 'source')
      return 'taps'

  }, [selectedEntity, flowVersion])
  const mapping = useSelector(state => state.entities.mapping)
  const sourceData = useSelector(state => state.entities?.sourceData?.[flowId])
  const isFetchingMapping = useSelector(state => state.entities.isFetchingMapping)
  const error = useRef({})
  const [tenantMapping, setTenantMapping] = useState()
  const [catalogMap, setCatalogMap] = useState()
  const [loading, setLoading] = useState(false)
  const [isRefreshing, setRefreshing] = useState(false)
  const [hasMapped, setHasMapped] = useState(false)
  const dispatch = useDispatch()
  const checkDiscoverFailed = useMemo(() => {
    if (currentEntity && currentEntity.discover_task_status)
      return currentEntity.discover_task_status !== 'DISCOVER_FAILED';

    return true;
  }, [currentEntity]);

  useEffect(() => {
    if ((!currentEntity || !currentEntity.fieldMap || !currentEntity.catalog) && !sourceData) {
      if (selectedEntity?.tap)
        dispatch(fetchLinkedSource(selectedEntity?.tap, true));
      else if (selectedEntity?.target)
        dispatch(fetchLinkedTarget(selectedEntity?.target, true));
      else if (selectedEntity?.id)
        dispatch(fetchLinkedConnector(selectedEntity?.id, true))
    }
  }, []);


  useEffect(() => {
    if((checkDiscoverFailed && currentEntity?.discover_task_status === 'DISCOVER_STARTED') && !isRefreshing){
      onPoll();
    }
  },[currentEntity?.discover_task_status])

  useEffect(() => {
    if (
      (currentEntity?.catalog === null || JSON.stringify(currentEntity?.catalog) === JSON.stringify({})) &&
      (checkDiscoverFailed && currentEntity?.discover_task_status === 'DISCOVER_STARTED')  &&
      !isRefreshing
    ) {
      // If catalog is empty there should be an ongoing discover task
      onPoll();
      return;
    }

    if (
      currentEntity &&
      (JSON.stringify(currentEntity?.catalog) === JSON.stringify({}) || currentEntity.catalog === null) &&
      checkDiscoverFailed &&
      !sourceData
    ) {
      // Call discover
      retryDiscover()
    }
  }, [currentEntity]);

  useEffect(() => {
    if (sourceData) {
      let newSourceDataMap = {}
      Object.entries(sourceData).forEach(([key, value]) => {
        newSourceDataMap[key] = { name: key, selected: [...new Set(value.map((x) => x.name))] }
      })
      setCatalogMap(newSourceDataMap)
    } else
      if (currentEntity && currentEntity?.catalog && currentEntity?.catalog?.streams?.length > 0) {
        let newCatalogMap = {}
        currentEntity.catalog.streams.forEach(stream => {

          const properties = stream.schema?.properties

          const { stream: name } = stream

          const all = properties && getAllProperties(properties, []) || [];
          newCatalogMap[name] = { name, selected: [...new Set(all)] }
        })
        setCatalogMap(newCatalogMap)
      }
  }, [currentEntity, currentEntity?.catalog, sourceData])

  useEffect(() => {
    if (!hasMapped && tenantMapping && Object.keys(tenantMapping).filter(x => x?.split('/')?.[0]?.split('_')?.[1]).length > 0) {
      UIActions.loadMapping(tenantMapping, flowId)
      setHasMapped(true)
    }
  }, [tenantMapping])

  useEffect(() => {
    try {
      setLoading(true)
      dispatch(fetchTenantMapping(selectedEntity.tenant_id))
    } catch (e) {
      Alert('error', 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isFetchingMapping && mapping && flowId) {
      setTenantMapping(mapping[flowId] || {})
    }
  }, [mapping, flowId, isFetchingMapping])

  const onPoll = () => {
    if (isRefreshing) return;

    setRefreshing(true);

    const { future, cancel } = poll({
      request: () => {
        return currentEntity?.id ? pollDiscoverLinkedConnector(currentEntity?.id) : pollDiscoverLinkedSource(currentEntity?.tap);
      },
      pollingPeriod: 1000,
      shouldStop: ({ status }) => {
        return status == null || status == 'STOPPING' || status == 'DEPROVISIONING' || status == 'STOPPED';
      },
    });

    future
      .then(async (response) => {
        const entityType = currentEntity?.id ? 'connector' : currentEntity?.tap ? 'source' : 'target'
        // get the latest discover task timestamp
        const action =
          entityType === 'connector'
            ? await dispatch(fetchLinkedConnector(currentEntity?.id, true))
            : entityType === 'source'
              ? await dispatch(fetchLinkedSource(currentEntity?.tap, true))
              : await dispatch(fetchLinkedTarget(currentEntity?.target, true))
        const entity = action?.payload?.length > 0 ? action?.payload[0] : null
        if (entity) dispatch({
          type: 'SELECT_ENTITY',
          payload: {
            ...entity,
            entityType,
          },
        });
        setRefreshing(false);
      })
      .catch((e) => {
        setRefreshing(false);
      });

  };

  const handleSave = async () => {
    setLoading(true)
    try {
      const entityId = selectedEntity?.entityType === 'source'
        ? selectedEntity?.tap
        : selectedEntity?.entityType === 'target'
          ? selectedEntity?.target
          : selectedEntity?.id;

      // NOTE: We need to send the whole combination otherwise we lose data
      const currentMapped = {
        [flowId]: {
          ...tenantMapping
        }
      }
      const payload = Object.assign({}, mapping, currentMapped)
      cleanNonSchemaTableAndFields(payload)
      clearFieldMapSelection(entityType === 'connectors');
      checkRequired(payload)
      if (JSON.stringify(error.current) !== '{}')
        throw new Error('Required fields missing!')

      await dispatch(doUpdateTenantMapping(payload, entityType, entityId, selectedEntity.tenant_id))

      if (listener && listener.onCustomMappingSave) {
        listener.onCustomMappingSave(entityId, flowId, selectedEntity.tenant_id ?? tenantId);
      }
      Alert({ type: 'success', text: `Mapping saved.` });
    } catch (e) {
      if (e?.message?.toLowerCase()?.includes('required'))
        Alert({ type: 'error', text: e.message });
      else
        Alert({ type: 'error', text: `An error occurred while saving the mapping.` });
    } finally {
      if (updateFieldsOnMapping) {
        if (entityType === 'connectors')
          dispatch(fetchLinkedConnector(entityId, true))
        if (entityType === 'target')
          dispatch(fetchLinkedTarget(entityId, true))
        if (entityType === 'taps')
          dispatch(fetchLinkedSource(entityId, true))
      }
      setLoading(false)
    }
  }

  const checkRequired = useCallback((payload) => {
    const mappedFields = Object.entries(payload[flowId])
      .map(([tables, fields]) => {
        return Object.keys(fields).map((x) => `${tables.split('/')[0]}/${x}`);
      })
      .flat();
    const mappedTables = Object.keys(payload[flowId]).flat().map(table => table.split('/')[0])
    const requiredFields = [];
    error.current = {}
    schemas.forEach(({ table, required, fields }) => {
      const ids = fields.filter(x => x.required && (mappedTables.includes(table) || required)).map(x => `${table}/${x.id}`) ?? []
      ids.length > 0 && requiredFields.push(...ids)
    })
    schemas.filter(x => x.required).forEach(({ table: requiredTable }) => {
      if (mappedTables.includes(requiredTable))
        delete error.current[requiredTable]
      else
        error.current[requiredTable] = true
    })
    requiredFields.forEach(requiredField => {
      if (mappedFields.includes(requiredField))
        delete error.current[requiredField]
      else
        error.current[requiredField] = true
    }
    )
  }, [schemas, flowId]);

  const cleanNonSchemaTableAndFields = useCallback((payload) => {
    Object.keys(payload[flowId]).forEach(key => {
      // Check if schema table is valid
      const tableMapped = key.split('/')[0]
      const schema = schemas.find(schema => schema.table === tableMapped)

      // Check if mapped table is valid
      const mappedTo = key.split('/')[1]

      if (schema && mappedTo !== "null") {
        const fieldIds = schema.fields.map(field => field.id)
        Object.keys(payload[flowId][key]).forEach(mapped => {
          if (!fieldIds.includes(mapped)) {
            delete payload[flowId][key][mapped]
          }
        })
      } else {
        delete payload[flowId][key]
      }

    })

  }, [schemas])

  const retryDiscover = async () => {
    if (currentEntity?.target) await discoverLinkedTargetCatalog(currentEntity?.target);
    else if (currentEntity?.tap) await discoverLinkedSourceCatalog(currentEntity?.tap);
    else if (currentEntity?.id) await discoverLinkedConnectorCatalog(currentEntity?.id);

    onPoll();
  }

  if(!checkDiscoverFailed && !isRefreshing){
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          height: '90%',
          flexDirection: 'column',
        }}
      >
        <Warning sx={{ fontSize: '64px', color: ({ palette }) => palette.warning.main }} />
        <Typography variant="h1">No Fields Found</Typography>
        <Typography variant="body1" color="text.light" sx={{ textAlign: 'center' }} px={4}>
          {localization?.customMapping?.loadingDescription ?? 'Failed to load available fields. Please double-check your credentials or contact support.'}
        </Typography>
        <Button variant="contained" color="primary" sx={{px: 5}} onClick={retryDiscover}>
          Retry
        </Button>
      </Box>
    );
  }


  if (isRefreshing || !currentEntity || currentEntity?.discover_task_status === 'DISCOVER_STARTED')
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          height: '90%',
          flexDirection: 'column',
        }}
      >
        <CircularProgress />
        <Typography variant="h1">{localization?.customMapping?.loadingTitle ?? 'Fetching available data...'}</Typography>
        <Typography variant="body1" color="text.light">
          {localization?.customMapping?.loadingDescription ?? "Hang tight, it's going to be quick."}
        </Typography>
      </Box>
    );


  if (!schemas || (!currentEntity?.catalog && !sourceData) || !tenantMapping || loading || !catalogMap || isFetchingMapping) {
    return <Box display='flex' justifyContent='space-between' flexDirection='column' width='100%' gap={2}>
      <Box
        display="flex"
        flexDirection="column"
        flex="1"
        width="100%"
        overflow="auto"
        gap={2}
      >
        {([...Array(3)].map((e, i) =>
          <Box key={i} display='flex' justifyContent='space-between' flexDirection='column' width='100%' gap={2}>
            <Box display='flex' flexDirection='column'
              p={'24px 24px 14px 16px'}
              sx={{
                border: ({ palette }) => `1px solid ${palette.common.outline}`
              }} borderRadius={2}>
              <Box display='flex' width='100%' gap={2} justifyContent='space-between'>
                <Box display='flex' gap={2} width={'50%'}>
                  <Skeleton width={32} sx={{ minWidth: '32px' }} height={32} variant='circular' />
                  <Box display='flex' flexDirection='column' width='100%'>
                    <Skeleton width='30%' height='16px' />
                    <Skeleton width='25%' height='16px' />
                  </Box>
                </Box>

                <Skeleton width='80px' height='16px' sx={{ borderRadius: '5px' }} />
              </Box>
              <Box display='flex' gap={2}>
                <Skeleton width='45%' height='32px'></Skeleton>
                <Skeleton width='5%' height='32px'></Skeleton>
                <Skeleton width='45%' height='32px'></Skeleton>
              </Box>
            </Box>
          </Box>))}
      </Box>
    </Box>
  }
  
  return (
    <Box display="flex" justifyContent="space-between" flexDirection="column" width="100%" gap={2}>
      <Box
        display="flex"
        position="sticky"
        top={41.5}
        flex={1}
        maxHeight={'64px'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{
          background: 'white',
          zIndex: 1000,
          pb: 2,
        }}
      >
        <Button
          sx={{ fontWeight: 400, fontSize: '14px', px: 2 }}
          startIcon={isRefreshing ? <CircularProgress size={16} /> : <Refresh sx={{ fontSize: '16px' }} />}
          variant="outlined"
          color="text"
          onClick={retryDiscover}
          disabled={isRefreshing}
        >
          {' '}
          Refresh
        </Button>
        {!isRefreshing && selectedEntity?.discover_task_timestamp && (
          <Typography sx={{ fontSize: '14px', color: 'text.light' }}>
            Last refreshed {moment.utc(selectedEntity.discover_task_timestamp).local().fromNow()}
          </Typography>
        )}
      </Box>
      <Box display="flex" position="relative" flexDirection="column" flex="1" width="100%" gap={2}>
        {schemas.map((schema, index) => (
          <RowMap
            key={`${schema.table}-${index}`}
            table={schema.table}
            required={schema.required}
            type={schema?.type}
            error={error.current}
            sourceFields={catalogMap}
            map={tenantMapping}
            setMap={setTenantMapping}
            index={index}
          />
        ))}
      </Box>
      <Box
        display="flex"
        justifyContent={'space-between'}
        sx={{
          position: 'sticky',
          bottom: 0,
          py: '16px',
          background: 'white',
        }}
      >
        <Button variant="outlined" color="text" onClick={() => UIActions.closeWidget()}>
          Cancel
        </Button>
        <Box display="flex" gap={2}>
          {multipleTables && (
            <Button
              variant="outlined"
              color="text"
              sx={{ fontSize: '14px', px: 4 }}
              onClick={() => {
                UIActions.addSchemaTable(flowId);
                setHasMapped(true);
              }}
            >
              <Add sx={{ fontSize: '16px' }} />
              Add Table
            </Button>
          )}
          <Button
            onClick={handleSave}
            variant="contained"
            color="accent"
            sx={{ fontSize: '14px', px: 4 }}
            form="app-params-form"
            type="submit"
            disabled={loading}
          >
            {loading && <CircularProgress style={{ width: '16px', height: '16px' }} />}
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default Mapping
